import { ChangeDetectionStrategy, Component, computed, inject, OnInit } from '@angular/core';
import { NavigationError, NavigationStart, Router, RouterEvent, RouterOutlet } from '@angular/router';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { faCopy, faSquareMinus, faSquarePlus } from '@fortawesome/free-regular-svg-icons';
import {
    faAngleDown,
    faAngleUp,
    faArrowAltCircleDown,
    faArrowAltCircleUp,
    faArrowCircleLeft,
    faArrowCircleRight,
    faArrowDown,
    faArrowDownAZ,
    faArrowDownZA,
    faArrowsRotate,
    faArrowUp,
    faArrowUpAZ,
    faAt,
    faCheckSquare,
    faClipboardList,
    faCloud,
    faCog,
    faEllipsisVertical,
    faEnvelope,
    faExclamationTriangle,
    faFileExcel,
    faFilter,
    faGlobe,
    faHandshake,
    faHome,
    faHouse,
    faInfoCircle,
    faLaptop,
    faListAlt,
    faLock,
    faLockOpen,
    faPaperclip,
    faPenToSquare,
    faPhone,
    faProjectDiagram,
    faServer,
    faShieldHalved,
    faStar,
    faTag,
    faUser,
} from '@fortawesome/free-solid-svg-icons';
import { Toast } from 'primeng/toast';
import { filter } from 'rxjs';

import { VersionService } from '@advania-cosoftware/advania-versioning';
import { SessionService, SessionState } from '@advania-cosoftware/wolf-core';
import { LogService } from '@advania-cosoftware/wolf-legacy';

import { SessionStateComponent } from './features/sessions/session-state/session-state.component';

@Component({
    selector: 'app-root',
    templateUrl: './app.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RouterOutlet, Toast, SessionStateComponent],
})
export class AppComponent implements OnInit {
    private readonly _iconLibrary = inject(FaIconLibrary);
    private readonly _versionCheck = inject(VersionService);
    private readonly _logger = inject(LogService);
    private readonly _router = inject(Router);
    private readonly _session = inject(SessionService);

    protected readonly showOverlay = computed(() => {
        const state = this._session.state();
        return (
            state === SessionState.LoadingAuthentication ||
            state === SessionState.LoadingCustomerAccess ||
            state === SessionState.LoadingCustomerContext ||
            state === SessionState.CustomerContextError ||
            state === SessionState.AuthenticationError ||
            state === SessionState.CustomerAccessError ||
            state === SessionState.CustomerUnavailable
        );
    });

    async ngOnInit(): Promise<void> {
        this._router.events.pipe(filter((e) => e instanceof NavigationError)).subscribe((evt) => {
            const ne = evt as NavigationError;
            if (ne.error.toString().indexOf('Failed to fetch dynamically imported module') >= 0) {
                this._versionCheck.checkNow();
            }
        });

        this._logger.log('AppComponent.ngOnInit %s', 'Entering');

        this._router.events.pipe(filter((event) => event instanceof NavigationStart)).subscribe((event) => {
            const e = event as RouterEvent;
            this._logger.log('AppComponent.Navigation', e.url);
        });
    }

    constructor() {
        this._iconLibrary.addIcons(
            faUser,
            faPhone,
            faAt,
            faPaperclip,
            faCheckSquare,
            faExclamationTriangle,
            faInfoCircle,
            faArrowCircleLeft,
            faArrowCircleRight,
            faProjectDiagram,
            faServer,
            faCloud,
            faCopy,
            faSquarePlus,
            faSquareMinus,
            faEnvelope,
            faArrowDownZA,
            faArrowDownAZ,
            faArrowUpAZ,
            faTag,
            faHandshake,
            faLaptop,
            faGlobe,
            faHome,
            faEllipsisVertical,
            faFilter,
            faCog,
            faArrowAltCircleDown,
            faArrowAltCircleUp,
            faArrowDown,
            faArrowUp,
            faAngleUp,
            faAngleDown,
            faShieldHalved,
            faPenToSquare,
            faLock,
            faLockOpen,
            faClipboardList,
            faListAlt,
            faFileExcel,
            faArrowsRotate,
            faHouse,
            faStar,
        );
    }
}
